import React, { useContext, useEffect, useState, useRef } from 'react';

import { useMutation } from '@apollo/react-hooks';

import TemplatesWizardContext from '../../../TemplatesWizardContext';

import styles from './AIAnalysingFile.module.css';
import aiUploadAnalysis from '../../../../../../../../../assets/img/templateswizard/spinner_doc_analyse.gif';
import aiUploadAnalysisComplete from '../../../../../../../../../assets/img/templateswizard/ai_upload_analysis_complete.png';
import {
  FIND_MATCHING_PROFILING_QUESTIONS,
  GET_SURVEY_STRUCTURE_FROM_FILE_WITH_AI
} from '../../../../../../../../../graphql/Survey';
import { TEMPLATES_WIZARD_STEP_KEYS } from '../../../../../helpers/constants';
import TextRotator from './components/TextRotator/TextRotator';
import prepareSurveyToLoadInState from '../../../../../helpers/prepareSurveyToLoadInState';

export default function() {
  const { steps, strings, surveyId, setWizardHidden, actions } = useContext(
    TemplatesWizardContext
  );

  const [aiAnalysisCompleted, setAiAnalysisCompleted] = useState(false);
  // The polling chains run in setTimeout closures from the first render, so
  // they never see fresh state values - completion is mirrored into a ref
  // (always current) and pending timeouts are kept so they can be cleared
  const analysisCompletedRef = useRef(false);
  const pollTimeoutIdsRef = useRef([]);
  const isMountedRef = useRef(true);
  const [
    profilingQuestionsProcessCompleted,
    setProfilingQuestionsProcessCompleted
  ] = useState(false);

  const [aiAnalysisOutput, setAiAnalysisOutput] = useState(null);
  const [getSurveyStructureFromFileWithAI] = useMutation(
    GET_SURVEY_STRUCTURE_FROM_FILE_WITH_AI
  );
  const [findMatchingProfilingQuestions] = useMutation(
    FIND_MATCHING_PROFILING_QUESTIONS
  );

  const onViewSurveyClick = () => {
    if (aiAnalysisCompleted) {
      actions.aiAnalysisToSurvey(aiAnalysisOutput);
      setWizardHidden(true);
    }
  };

  const getSurveyStructureFromFileWithAIRequest = async (
    requestIndex,
    numberOfReAttempts = 0
  ) => {
    const uploadFileDetails = steps.find(
      s => s.key === TEMPLATES_WIZARD_STEP_KEYS.UPLOAD_FILE
    );
    if (
      uploadFileDetails &&
      uploadFileDetails.value &&
      uploadFileDetails.value.fileName
    ) {
      // Start AI analysis
      const response = await getSurveyStructureFromFileWithAI({
        variables: {
          survey: surveyId,
          fileName: uploadFileDetails.value.fileName,
          requestIndex
        }
      });

      if (
        isMountedRef.current &&
        !analysisCompletedRef.current &&
        response.data.getSurveyStructureFromFileWithAI.status === 'PROCESSING'
      ) {
        pollTimeoutIdsRef.current.push(
          setTimeout(() => {
            // Poll AI analysis
            getSurveyStructureFromFileWithAIRequest(
              requestIndex + 1,
              numberOfReAttempts
            );
          }, 3000)
        );
      }

      if (response.data.getSurveyStructureFromFileWithAI.status === 'FAILED') {
        if (numberOfReAttempts < 3) {
          getSurveyStructureFromFileWithAIRequest(0, numberOfReAttempts + 1);
          return;
        }
        alert(
          `Error! Please contact administrator! reason: ${
            response.data.getSurveyStructureFromFileWithAI.error
          }`
        );
      }

      if (
        response.data.getSurveyStructureFromFileWithAI.status === 'COMPLETED'
      ) {
        const cleanJsonString = response.data.getSurveyStructureFromFileWithAI.output
          .replace(/^```json\s*|```\s*$/g, '')
          .trim();
        const dataObject = JSON.parse(cleanJsonString);

        const surveyToSet = prepareSurveyToLoadInState(dataObject);
        if (surveyToSet === false && numberOfReAttempts < 3) {
          getSurveyStructureFromFileWithAIRequest(0, numberOfReAttempts + 1);
          return;
        }

        setAiAnalysisCompleted(true);
        setAiAnalysisOutput(dataObject);

        const profilingQuestions =
          dataObject &&
          dataObject.questions &&
          dataObject.questions.filter(
            q => q && q.profiling && q.profiling.isProfilingQuestion
          );

        if (profilingQuestions && profilingQuestions.length) {
          await replaceMatchingProfilingQuestionsRequest(0, dataObject);
          return;
        }

        setProfilingQuestionsProcessCompleted(true);
      }
    } else {
      setAiAnalysisCompleted(true);
      setProfilingQuestionsProcessCompleted(true);
    }
  };

  const replaceMatchingProfilingQuestionsRequest = async (
    // eslint-disable-next-line default-param-last
    requestIndex = 0,
    aiAnalysisInput,
    numberOfReAttempts = 0
  ) => {
    const profilingQuestions =
      aiAnalysisInput &&
      aiAnalysisInput.questions &&
      aiAnalysisInput.questions.filter(
        q => q && q.profiling && q.profiling.isProfilingQuestion
      );

    const preparedProfilingQuestions =
      profilingQuestions &&
      profilingQuestions.map(q => ({
        id: q.id,
        question: q.question
      }));

    if (profilingQuestions && profilingQuestions.length) {
      // Start AI profiling question match
      const response = await findMatchingProfilingQuestions({
        variables: {
          survey: surveyId,
          requestIndex,
          language: 'Dutch',
          questions: preparedProfilingQuestions
        }
      });

      if (
        isMountedRef.current &&
        !analysisCompletedRef.current &&
        response.data.findMatchingProfilingQuestions.status === 'PROCESSING'
      ) {
        pollTimeoutIdsRef.current.push(
          setTimeout(() => {
            // Poll AI analysis
            replaceMatchingProfilingQuestionsRequest(
              requestIndex + 1,
              aiAnalysisInput,
              numberOfReAttempts
            );
          }, 3000)
        );
      }

      if (response.data.findMatchingProfilingQuestions.status === 'FAILED') {
        if (numberOfReAttempts < 3) {
          replaceMatchingProfilingQuestionsRequest(
            0,
            aiAnalysisInput,
            numberOfReAttempts + 1
          );
          return;
        }

        alert(
          `Error! Please contact administrator! reason: ${
            response.data.findMatchingProfilingQuestions.error
          }`
        );
      }

      if (response.data.findMatchingProfilingQuestions.status === 'COMPLETED') {
        setAiAnalysisCompleted(true);

        const cleanJsonString = response.data.findMatchingProfilingQuestions.output
          .replace(/^```json\s*|```\s*$/g, '')
          .trim();
        const dataObject = JSON.parse(cleanJsonString);

        if (
          dataObject &&
          dataObject.length &&
          aiAnalysisInput &&
          aiAnalysisInput.questions &&
          aiAnalysisInput.questions.length
        ) {
          const modifiedAiAnalysisOutputQuestions = aiAnalysisInput.questions.map(
            q => {
              if (
                q &&
                q.type === 'mc_question' &&
                q.profiling &&
                q.profiling.isProfilingQuestion
              ) {
                const matchingProfilingQuestion = dataObject.find(
                  dO => dO.originalQuestionId.toString() === q.id.toString()
                );

                const everyChoiceFromMatchedQuestionMatch =
                  q &&
                  q.choices &&
                  q.choices.length &&
                  matchingProfilingQuestion &&
                  matchingProfilingQuestion.choices &&
                  matchingProfilingQuestion.choices.length &&
                  q.choices.length ===
                    matchingProfilingQuestion.choices.length &&
                  q.choices.every(
                    (obj, i) =>
                      obj.answer &&
                      matchingProfilingQuestion.choices[i].answer &&
                      obj.answer.trim() ===
                        matchingProfilingQuestion.choices[i].answer.trim()
                  );

                if (
                  matchingProfilingQuestion &&
                  everyChoiceFromMatchedQuestionMatch
                ) {
                  const newQ = {
                    ...q,
                    question: q.question,
                    profiling: {
                      ...matchingProfilingQuestion.profiling,
                      isReusedProfilingQuestion: true
                    }
                  };

                  newQ.choices = q.choices.map(c => {
                    const newC = { ...c };

                    const matchingChoiceFromAi = matchingProfilingQuestion.choices.find(
                      ch =>
                        ch.answer &&
                        c.answer &&
                        ch.answer.trim() === c.answer.trim()
                    );

                    if (matchingChoiceFromAi) {
                      newC.profilingTag = matchingChoiceFromAi.profilingTag;
                    }

                    delete newC._id;
                    return newC;
                  });

                  delete newQ._id;
                  return newQ;
                }

                // No criteria fulfilled for this question to be profiling question
                const newQ = {
                  ...q,
                  profiling: {
                    ...q.profiling,
                    isProfilingQuestion: false,
                    isReusedProfilingQuestion: false,
                    visibleForSearch: true,
                    language: null,
                    id: null
                  }
                };

                newQ.choices = q.choices.map(c => {
                  const newC = {
                    ...c,
                    profilingTag: null
                  };

                  delete newC._id;
                  return newC;
                });

                delete newQ._id;
                return newQ;
              }

              // eslint-disable-next-line no-param-reassign
              delete q._id;
              return q;
            }
          );

          setAiAnalysisOutput({
            ...aiAnalysisInput,
            questions: modifiedAiAnalysisOutputQuestions
          });
        }

        setProfilingQuestionsProcessCompleted(true);
      }
    }
  };

  // Keep the ref in sync so the polling closures see the current value
  useEffect(
    () => {
      analysisCompletedRef.current =
        aiAnalysisCompleted && profilingQuestionsProcessCompleted;
    },
    [aiAnalysisCompleted, profilingQuestionsProcessCompleted]
  );

  useEffect(() => {
    isMountedRef.current = true;
    getSurveyStructureFromFileWithAIRequest(0, 0);

    return () => {
      // Stop pending and in-flight polls when the component unmounts
      isMountedRef.current = false;
      pollTimeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      pollTimeoutIdsRef.current = [];
    };
  }, []);

  const aiAnalyticsRotatorText = [
    strings.TRANSLATING_PARAGRAPHS_INTO_QUESTIONS,
    strings.TURNING_BULLET_POINTS_INTO_ANSWER_OPTIOSN,
    strings.ERASING_ACCIDENTAL_ENTERS,
    strings.UNTANGLING_YOUR_DOCUMENT_FORMATTING,
    strings.POLISHING_YOUR_LIKER_SCALES,
    strings.CALCULATING_THE_PERFECT_QUESTION,
    strings.ENSURING_NOBODY_SKIPS_QUESTIONS,
    strings.IRONING_OUT_YOUR_MATRIX,
    strings.CALCULATING_THE_PERFECT_QUESTION_FLOW,
    strings.INSERTING_LOGIC_BRANCHES
  ];

  const analysisCompleted =
    aiAnalysisCompleted && profilingQuestionsProcessCompleted;

  return (
    <div className={styles.container}>
      <div className={styles.title}>{strings.UPLOAD_YOUR_DOCUMENT}</div>
      <div className={styles.subTitle}>
        {strings.WE_ARE_SETTING_YOUR_SURVEY_IN_REAL_TIME}
      </div>
      <div className={styles.textContainer}>
        {analysisCompleted ? (
          <div className={styles.infoContainer}>
            <img
              src={aiUploadAnalysisComplete}
              className={styles.uploadIcon}
              alt="Ai Upload Complete analysis icon"
            />
            <div className={styles.title1}>{strings.SURVEY_BUILD_COMPLETE}</div>
            <div className={styles.title2}>
              {strings.YOUR_DOCUMENT_HAS_BEEN_SUCCESSFULLY_BEEN_CONVERTED}
            </div>
            <div className={styles.title2}>
              {strings.PLEASE_DONT_CLOSE_THIS_WINDOW}
            </div>
          </div>
        ) : (
          <div className={styles.infoContainer}>
            <img
              src={aiUploadAnalysis}
              className={styles.uploadIconAnimation}
              alt="Ai Upload analysis icon"
            />
            <div className={styles.title1}>
              {strings.ANALYSING_YOUR_DOCUMENT}
            </div>
            <TextRotator items={aiAnalyticsRotatorText} />
          </div>
        )}

        <div className={styles.viewSurveyButtonContainer}>
          <div
            role="presentation"
            onClick={onViewSurveyClick}
            className={
              analysisCompleted
                ? styles.viewSurveyButton
                : `${styles.viewSurveyButton} ${styles.disabled}`
            }
          >
            {strings.VIEW_SURVEY}
          </div>
        </div>
      </div>
    </div>
  );
}
